import { Link } from "react-router-dom";
import { JOURNEYS } from "../lib/journeys";
import { usePageMeta } from "../lib/pageMeta";
import { JourneyGlyph } from "../components/JourneyGlyph";

/**
 * Cierge Labs — Journey Storybook index.
 *
 * Single curated entry point listing illustrative journeys. Today: one
 * journey (Governed Agentic Procurement). Designed so adding future
 * journeys is a one-line append to `lib/journeys.js` — no nav, footer or
 * landing-page changes required.
 *
 * Every card carries its honest status badge using the existing Cierge
 * Labs / platform-site taxonomy. No live claims are made unless the entry
 * is `status: "Live KATLAS node"`.
 */

const STATUS_STYLE = {
  "Illustrative reference architecture": {
    color: "#C19A4A",
    border: "border-[#C19A4A]/40",
    text: "text-[#C19A4A]",
    bg: "bg-white/5",
  },
  "Concept": {
    color: "#7FB2FF",
    border: "border-[#7FB2FF]/40",
    text: "text-[#7FB2FF]",
    bg: "bg-white/5",
  },
  "Simulation": {
    color: "#7FB2FF",
    border: "border-[#7FB2FF]/40",
    text: "text-[#7FB2FF]",
    bg: "bg-white/5",
  },
  "Live KATLAS node": {
    color: "#3FB37F",
    border: "border-[#3FB37F]/40",
    text: "text-[#3FB37F]",
    bg: "bg-white/5",
  },
};

export const Journeys = () => {
  usePageMeta({
    title: "Journey Storybook — Cierge Labs",
    description:
      "The Cierge Labs Journey Storybook — curated illustrative reference journeys showing how KATLAS governs AI-assisted, multi-party workflows across sectors.",
    ogTitle: "Journey Storybook — Cierge Labs",
    ogDescription:
      "Curated illustrative reference journeys showing how KATLAS governs AI-assisted, multi-party workflows.",
    ogUrl: "https://labs.katlastechnology.com/journeys",
  });

  return (
    <div data-testid="journeys-index" className="bg-white">
      {/* HERO */}
      <section
        data-testid="journeys-hero"
        className="relative overflow-hidden border-b border-border text-white"
        style={{
          background:
            "linear-gradient(180deg, #061A2F 0%, #0A2540 55%, #0A2540 100%)",
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "100% 96px",
          }}
        />
        <div className="relative mx-auto max-w-[1080px] px-6 py-20 md:py-24 lg:px-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#7FB2FF]">
            Cierge Labs · Journey storybook
          </p>
          <h1
            data-testid="journeys-headline"
            className="mt-6 max-w-3xl font-serif text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-6xl"
          >
            Curated journeys above the KATLAS governance layer.
          </h1>
          <p
            data-testid="journeys-sub"
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/75"
          >
            Each journey is an illustrative reference. Status is named
            honestly: an entry is only marked as a live KATLAS node when a
            live KATLAS-signed receipt is emitted by it. Everything else is
            clearly labelled as an architecture, concept or simulation.
          </p>
        </div>
      </section>

      {/* CARDS */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1080px] px-6 py-16 md:py-20 lg:px-10">
          <ul className="grid gap-px bg-border md:grid-cols-2">
            {JOURNEYS.map((j) => {
              const style =
                STATUS_STYLE[j.status] ||
                STATUS_STYLE["Illustrative reference architecture"];
              return (
                <li
                  key={j.slug}
                  data-testid={`journey-card-${j.slug}`}
                  className="bg-white p-8"
                >
                  <Link
                    to={j.href}
                    className="group block focus:outline-none"
                    data-testid={`journey-card-link-${j.slug}`}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <span
                        className={`inline-flex items-center gap-2 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${style.border} ${style.text} ${style.bg}`}
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: style.color }}
                        />
                        <span>{j.status}</span>
                      </span>
                      <div
                        className="shrink-0 opacity-80 transition-opacity group-hover:opacity-100"
                        aria-hidden="false"
                      >
                        <JourneyGlyph sector={j.primarySector} />
                      </div>
                    </div>
                    <h2 className="mt-5 font-serif text-3xl font-light leading-tight tracking-tight text-foreground transition-colors group-hover:text-[#0A2540]">
                      {j.title}
                    </h2>
                    <p className="mt-2 font-serif text-lg font-light italic text-muted-foreground">
                      {j.subtitle}
                    </p>
                    <p className="mt-5 text-sm leading-relaxed text-foreground/80">
                      {j.summary}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      <span className="text-[#0A2540]">
                        › {j.primarySector}
                      </span>
                      {j.secondaryTags.map((t) => (
                        <span key={t}>· {t}</span>
                      ))}
                    </div>
                    <span className="mt-8 inline-flex items-center gap-2 border-b border-foreground/30 pb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground transition-colors group-hover:border-foreground">
                      Open journey →
                    </span>
                  </Link>
                </li>
              );
            })}

            {/* honest empty-slot note when only one journey is published */}
            {JOURNEYS.length === 1 && (
              <li
                data-testid="journeys-coming-soon-note"
                className="bg-[#FAFAFA] p-8"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  More journeys
                </p>
                <p className="mt-5 font-serif text-2xl font-light leading-tight tracking-tight text-foreground/70">
                  Additional illustrative journeys are being prepared in
                  Cierge Labs.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Future entries will appear here once they are ready — each
                  with an honest status badge. No journey is listed as a live
                  KATLAS node unless it actually is one.
                </p>
              </li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Journeys;
